
import { useState, useEffect, useRef } from 'react';
import { BookOpen, CloudUpload, FileEdit, Send, UploadCloud, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, Field, Input, Textarea, Tooltip, confirmDialog, toast } from '@/shared/ui';
import { apiErrorText } from '@/shared/api';
import { draftsApi, type TicketDraft } from '@/shared/api/drafts';
import { useTicketsStore } from '../model/tickets-store';
import { useDraftStore } from '../model/draft-store';
import { draftEntryPath } from '../model/draft-entry';
import { AttachmentPicker, appendFiles, clipboardFiles, uploadDraftAttachments } from './AttachmentPicker';
import { formsApi, validateFormValues, type FormValues, type FormVersionInfo } from '@/shared/api/forms';
import { DynamicFormFields } from './DynamicFormFields';
import type { Ticket } from '@/entities/ticket';
import { MAX_TICKET_DESCRIPTION_LENGTH } from '@/shared/config';
import { formatQuote, useQuoteStore, type WikiQuote } from '@/features/wiki';
import styles from './CreateTicketForm.module.css';

function stripQuotePrefix(description: string, quote: WikiQuote | null): string {
  if (!quote) return description;
  const prefix = formatQuote(quote);
  return description.startsWith(prefix) ? description.slice(prefix.length) : description;
}

export interface CreateTicketFormProps {
  defaultSubcategoryId?: string;
  onSuccess?: () => void;
}

export const CreateTicketForm: React.FC<CreateTicketFormProps> = ({
  defaultSubcategoryId = '',
  onSuccess,
}) => {
  const freeForm = !defaultSubcategoryId;
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const quote = useQuoteStore((s) => s.quote);
  const clearQuote = useQuoteStore((s) => s.clearQuote);
  const composedDescription = quote ? `${formatQuote(quote)}${description}` : description;
  const [subcategoryId, setSubcategoryId] = useState<string>(defaultSubcategoryId);
  const [submitting, setSubmitting] = useState<'send' | 'draft' | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [formVersion, setFormVersion] = useState<FormVersionInfo | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrorsShown, setFormErrorsShown] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);
  const [restoredNotice, setRestoredNotice] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dragCounter = useRef(0);
  const fetchTickets = useTicketsStore((s) => s.fetchTickets);

  const lastSavedRef = useRef<string | null>(null);
  const touchedRef = useRef(false);
  const setStoreDraft = useDraftStore((s) => s.setDraft);
  const [conflictDraft, setConflictDraft] = useState<TicketDraft | null>(null);
  const conflictAskedRef = useRef(false);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const queueDraftOp = (op: () => Promise<void>) => {
    const next = saveQueueRef.current.catch(() => {}).then(op);
    saveQueueRef.current = next.catch(() => {});
    return next;
  };

  const prevSubcategoryRef = useRef(subcategoryId);
  useEffect(() => {
    if (prevSubcategoryRef.current !== subcategoryId) {
      prevSubcategoryRef.current = subcategoryId;
      setFormVersion(null);
      setFormValues({});
      setFormErrorsShown(false);
    }
    if (!subcategoryId) {
      setFormVersion(null);
      setFormValues({});
      return;
    }
    let cancelled = false;
    formsApi
      .getPublished(subcategoryId)
      .then((res) => {
        if (cancelled) return;
        setFormVersion((res.data as FormVersionInfo | null) ?? null);
      })
      .catch(() => !cancelled && setFormVersion(null));
    return () => {
      cancelled = true;
    };
  }, [subcategoryId]);

  const persistDraft = (fields: {
    title: string;
    description: string;
    subcategoryId: string;
    formValues: FormValues;
  }) =>
    queueDraftOp(async () => {
      const snapshot = JSON.stringify(fields);
      if (snapshot === lastSavedRef.current) return;
      const res = await draftsApi.save({
        ...(fields.title.trim() && { subject: fields.title.trim() }),
        description: fields.description,
        ...(fields.subcategoryId && { subcategoryId: fields.subcategoryId }),
        formData: fields.formValues,
      });
      setStoreDraft(res.data ?? null);
      lastSavedRef.current = snapshot;
      setDraftSavedAt(Date.now());
    });

  useEffect(() => {
    if (conflictDraft) return;
    if ((!subcategoryId && !title.trim()) || !composedDescription.trim()) return;
    const timer = setTimeout(() => {
      persistDraft({
        title,
        description: composedDescription,
        subcategoryId,
        formValues,
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, composedDescription, subcategoryId, formValues, conflictDraft]);

  const askReplaceConflict = (draft: TicketDraft) =>
    confirmDialog({
      title: 'Заменить незаконченную заявку?',
      description: `У вас уже есть черновик${
        draft.subject ? ` — «${draft.subject}»` : ' по другой услуге'
      }. Черновик хранится один: если продолжить здесь, прежний будет заменён.`,
      confirmLabel: 'Заменить этой',
      cancelLabel: 'Оставить прежний',
      danger: true,
    });

  useEffect(() => {
    if (!conflictDraft || conflictAskedRef.current) return;
    const started =
      composedDescription.trim().length > 0 ||
      title.trim().length > 0 ||
      Object.keys(formValues).length > 0;
    if (!started) return;
    conflictAskedRef.current = true;
    void (async () => {
      const replace = await askReplaceConflict(conflictDraft);
      if (replace) setConflictDraft(null);
    })();
  }, [conflictDraft, composedDescription, title, formValues]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSubcategoryId(defaultSubcategoryId);
    setFormValues({});
    setFormErrorsShown(false);
    setFiles([]);
    setDraftSavedAt(null);
    setRestoredNotice(false);
    clearQuote();
    lastSavedRef.current = null;
  };


  useEffect(() => {
    let cancelled = false;
    draftsApi
      .get()
      .then((res) => {
        const draft = res.data ?? null;
        if (cancelled || touchedRef.current || !draft) return;
        setStoreDraft(draft);
        const sameTarget = (draft.subcategoryId ?? '') === (defaultSubcategoryId || '');
        const adopt = !freeForm && !draft.subcategoryId;
        if (!sameTarget && !adopt) {
          setConflictDraft(draft);
          return;
        }
        const draftFormValues = (draft.formData as FormValues | null) ?? {};
        const draftTitle = freeForm ? draft.subject ?? '' : '';
        lastSavedRef.current = adopt
          ? null
          : JSON.stringify({
              title: draftTitle,
              description: draft.description ?? '',
              subcategoryId: draft.subcategoryId ?? '',
              formValues: draftFormValues,
            });
        setFormValues(draftFormValues);
        setTitle(draftTitle);
        setDescription(stripQuotePrefix(draft.description ?? '', useQuoteStore.getState().quote));
        setSubcategoryId(defaultSubcategoryId || draft.subcategoryId || '');
        setDraftSavedAt(new Date(draft.updatedAt).getTime());
        setRestoredNotice(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discardDraft = async () => {
    resetForm();
    setConflictDraft(null);
    conflictAskedRef.current = true;
    setStoreDraft(null);
    try {
      await draftsApi.discard();
    } catch {
    }
  };

  useEffect(() => {
    if (defaultSubcategoryId) setSubcategoryId(defaultSubcategoryId);
  }, [defaultSubcategoryId]);

  const canSubmit =
    Boolean(composedDescription.trim()) && Boolean(freeForm ? title.trim() : subcategoryId);

  const submit = async (asDraft: boolean) => {
    if (submitting) return;
    if (!asDraft && formVersion) {
      const errors = validateFormValues(formVersion.schema, formValues);
      if (errors.length > 0) {
        setFormErrorsShown(true);
        toast.error(errors[0]);
        return;
      }
    }
    if (conflictDraft) {
      const replace = await askReplaceConflict(conflictDraft);
      if (!replace) return;
      setConflictDraft(null);
    }
    setSubmitting(asDraft ? 'draft' : 'send');
    try {
      await persistDraft({
        title,
        description: composedDescription,
        subcategoryId,
        formValues,
      });
      if (asDraft) {
        if (files.length > 0) {
          toast.warning('Файлы не сохраняются в черновике — приложите их при подаче');
        }
        toast.success('Черновик сохранён — вернётесь к нему по кнопке «Новое обращение»');
      } else {
        const res = await draftsApi.submit();
        const ticketId = (res.data as Ticket).id;
        setStoreDraft(null);
        if (files.length > 0) {
          try {
            await uploadDraftAttachments(ticketId, files);
            setFiles([]);
          } catch {
            toast.error('Не все файлы загрузились — прикрепите их в чате заявки');
          }
        }
      }
      resetForm();
      void fetchTickets();
      onSuccess?.();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось отправить заявку'));
    } finally {
      setSubmitting(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit(false);
  };

  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (submitting !== null || e.defaultPrevented) return;
      const target = e.target;
      const inForm = target instanceof Node && formRef.current?.contains(target);
      if (!inForm && target !== document.body && target !== document.documentElement) return;
      const list = clipboardFiles(e.clipboardData, target);
      if (!list) return;
      e.preventDefault();
      appendFiles(files, list, setFiles);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [files, submitting]);

  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types).includes('Files');

  const handleDragEnter = (e: React.DragEvent) => {
    if (submitting !== null || !hasFiles(e)) return;
    e.preventDefault();
    dragCounter.current += 1;
    setDragOver(true);
  };
  const handleDragLeave = () => {
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragOver(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    if (submitting === null && e.dataTransfer.files.length) {
      appendFiles(files, e.dataTransfer.files, setFiles);
    }
  };

  return (
    <form
      ref={formRef}
      className={styles.form}
      onSubmit={handleSubmit}
      onDragEnter={handleDragEnter}
      onDragOver={(e) => hasFiles(e) && e.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className={styles.dropOverlay}>
          <UploadCloud size={36} />
          Отпустите файлы, чтобы прикрепить
        </div>
      )}

      {conflictDraft && (
        <div className={styles.restoredNotice} role="status">
          <FileEdit size={14} aria-hidden />
          <span>
            {conflictDraft.subject
              ? `Незаконченная заявка: «${conflictDraft.subject}»`
              : 'У вас есть незаконченная заявка по другой услуге'}
          </span>
          <Link
            className={styles.restoredDismiss}
            to={draftEntryPath(conflictDraft)}
          >
            Вернуться к ней
          </Link>
        </div>
      )}

      {restoredNotice && (
        <div className={styles.restoredNotice} role="status">
          <FileEdit size={14} aria-hidden />
          <span>Черновик восстановлен</span>
          <Tooltip content="Удалить черновик и начать заново">
            <button
              type="button"
              className={styles.restoredDismiss}
              onClick={() => void discardDraft()}
            >
              <X size={13} aria-hidden />
              Начать заново
            </button>
          </Tooltip>
        </div>
      )}

      {freeForm && (
        <div className={styles.field}>
          <Field label="Тема заявки" required>
            <Input
              id="ticket-title"
              value={title}
              maxLength={200}
              onChange={(e) => {
                touchedRef.current = true;
                setTitle(e.target.value);
              }}
              placeholder="Коротко о проблеме — например, «Не открывается 1С»"
              required
            />
          </Field>
        </div>
      )}

      {formVersion && (
        <DynamicFormFields
          schema={formVersion.schema}
          values={formValues}
          onChange={(next) => {
            touchedRef.current = true;
            setFormValues(next);
          }}
          disabled={submitting !== null}
          showErrors={formErrorsShown}
          progressive
        />
      )}

      <div className={styles.field}>
        {quote && (
          <div className={styles.quoteCard}>
            <BookOpen size={15} aria-hidden />
            <span className={styles.quoteCardBody}>
              <span className={styles.quoteCardTitle}>{quote.title}</span>
              <span className={styles.quoteCardText}>{quote.text}</span>
            </span>
            <Tooltip content="Убрать цитату">
              <button
                type="button"
                className={styles.quoteCardClose}
                onClick={clearQuote}
                aria-label="Убрать цитату"
              >
                <X size={14} aria-hidden />
              </button>
            </Tooltip>
          </div>
        )}
        <div className={styles.descriptionWrap}>
        { }
        <Field label="Описание" required>
          <Textarea
            id="ticket-description"
            className={styles.description}
            value={description}
            maxLength={MAX_TICKET_DESCRIPTION_LENGTH}
            onChange={(e) => {
              touchedRef.current = true;
              setDescription(e.target.value);
            }}
            placeholder="Опишите проблему: что делали, что ожидали и что произошло…"
            required
          />
        </Field>
        {description.length > MAX_TICKET_DESCRIPTION_LENGTH * 0.8 && (
          <span className={styles.charCounter} aria-live="polite">
            {description.length} / {MAX_TICKET_DESCRIPTION_LENGTH}
          </span>
        )}
        </div>
      </div>

      <AttachmentPicker files={files} onChange={setFiles} disabled={submitting !== null} pasteHint />

      <div className={styles.submitRow}>
        <span className={styles.savedHint} aria-live="polite">
          {draftSavedAt && (
            <>
              <CloudUpload size={13} aria-hidden />
              Черновик сохранён в{' '}
              {new Date(draftSavedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            </>
          )}
        </span>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void submit(true)}
          disabled={submitting !== null || !canSubmit}
          tooltip="Сохранится черновиком — вернётесь к нему по кнопке «Новое обращение». Файлы придётся приложить заново"
        >
          {submitting === 'draft' ? 'Сохранение…' : 'В черновики'}
        </Button>
        <Button type="submit" variant="primary" disabled={submitting !== null || !canSubmit}>
          <Send size={15} aria-hidden className={styles.sendIcon} />
          {submitting === 'send' ? 'Отправка…' : 'Отправить'}
        </Button>
      </div>
    </form>
  );
};
